import { setExtras } from '@sentry/react';
import { ImmerReducer } from 'immer-reducer';

import { TimeSlot } from '../api/api';
import { FormValues } from '../components/form/utils';
import { InitMessageParams } from '../components/InitApp/types';
import { Codeowners } from '../consts/codeowners';
import { FormattedServicesData } from '../hooks/useServices';
import { FIRST_STEP_KEY, StepNames } from '../Steps/consts';
import { UNITED_STATES } from '../Steps/ContactDetailsStep/consts';
import type { CategoryType, IndustryType, PricingFormType, ServiceType, SubCategoryType } from '../types/types';
import { captureMessage } from '../utils/exception';

import type { ContactDetailsValues, Photo, PricingFormValue, ServiceCartItem, State } from './types';
import { findCartItem, isServiceCartItem, prepareAWSKey } from './utils';

const DEFAULT_COUNTRY = 'United States';

export const initialState: State = {
  previousStepKey: FIRST_STEP_KEY,
  stepKey: FIRST_STEP_KEY,
  industry: undefined,
  category: undefined,
  subcategory: undefined,
  service: undefined,
  pricingForm: undefined,
  description: '',
  question1: null,
  question2: null,
  contactDetails: {
    submitting: false,
    valid: false,
    values: {
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      country: DEFAULT_COUNTRY,
      addressLine1: '',
      addressLine2: '',
      state: '',
      city: '',
      zipCode: '',
      consent: false,
    },
  },
  draftQuantity: 1,
  cart: [],
  token: '',
  success: false,
  unavailableDateError: false,
  arrivalWindow: {
    date: undefined,
    time: undefined,
  },
  firstStepSkipped: false,
  secondStepSkipped: false,
  photos: [],
  pricingFormValues: [],
  appOpened: false,
  warningMessage: null,
};

export class OnlineBookingReducer extends ImmerReducer<State> {
  resetStep() {
    this.draftState.previousStepKey = FIRST_STEP_KEY;
    this.draftState.stepKey = FIRST_STEP_KEY;
  }

  changeStep(step: StepNames[number]) {
    this.draftState.previousStepKey = this.draftState.stepKey;
    this.draftState.stepKey = step;
  }

  selectPricingForm(pricingForm: PricingFormType) {
    this.draftState.pricingForm = pricingForm;
  }

  selectIndustry(industry: IndustryType) {
    this.draftState.industry = industry;
  }

  selectCategory(category: CategoryType) {
    this.draftState.category = category;
  }

  selectSubCategory(subcategory?: SubCategoryType) {
    this.draftState.subcategory = subcategory;
  }

  resetSubCategory() {
    this.draftState.subcategory = undefined;
  }

  selectService(service: ServiceType) {
    this.draftState.service = service;
  }

  setDraftQuantity(quantity: number) {
    this.draftState.draftQuantity = quantity;
  }

  resetDraftQuantity() {
    this.draftState.draftQuantity = 1;
  }

  setAlert(alert: string) {
    this.draftState.alert = alert;
  }

  addToCart() {
    const { service, pricingForm } = this.draftState;

    if (service) {
      const cartItem = findCartItem(this.draftState.cart, service?.uuid) as ServiceCartItem;

      if (cartItem === undefined) {
        this.draftState.cart.push({
          type: 'service',
          service,
          quantity: this.draftState.draftQuantity,
          question_1: '',
          question_2: '',
          description: '',
          photos: [],
        });
      } else {
        cartItem.quantity += this.draftState.draftQuantity;
        this.resetDraftQuantity();
      }
    }

    if (pricingForm) {
      const cartItem = findCartItem(this.draftState.cart, pricingForm?.uuid);
      const formValues = this.draftState.pricingFormValues.find((item) => item.uuid === pricingForm.uuid);
      if (cartItem === undefined) {
        if (formValues) {
          this.draftState.cart.push({
            type: 'pricing-form',
            values: formValues?.values,
            pricingForm,
            question_1: '',
            question_2: '',
            description: '',
            photos: [],
          });
        }
      } else if (cartItem.type === 'pricing-form' && formValues) {
        cartItem.values = formValues?.values;
      }
    }
  }

  resetFlow() {
    this.draftState.service = undefined;
    this.draftState.category = undefined;
    this.draftState.subcategory = undefined;
    this.draftState.industry = undefined;
    this.draftState.pricingForm = undefined;
    this.resetDraftQuantity();
    this.resetStep();
  }

  resetFlowWithCart() {
    this.resetFlow();
    this.draftState.cart = [];
  }

  setCustomerBannedFlow() {
    this.draftState.customerBanned = true;
  }

  setWarningMessage(newMessage: string | null) {
    this.draftState.warningMessage = newMessage;
  }

  hideWarningMessage() {
    this.draftState.warningMessage = null;
  }

  removeFromCart(uuid: string) {
    const { cart } = this.draftState;
    const serviceCartItem = cart.find((item) => isServiceCartItem(item) && item.service.uuid === uuid);

    if (serviceCartItem) {
      const photosToDelete = serviceCartItem.photos.map((photo) => photo.file.name);
      const photosWithoutRemovedCartPhotos = this.draftState.photos.filter(
        (photoLink) => !photosToDelete.some((fileName) => photoLink.includes(fileName))
      );

      this.draftState.photos = photosWithoutRemovedCartPhotos;
      this.draftState.cart = cart.filter((item) => (isServiceCartItem(item) ? item.service.uuid !== uuid : true));
    }

    if (uuid === this.draftState.service?.uuid) {
      this.resetFlow();
    }
  }

  changeQuantity(uuid: string, newQuantity: number) {
    const cartItem = findCartItem(this.draftState.cart, uuid) as ServiceCartItem;
    if (cartItem) {
      cartItem.quantity = newQuantity;
    }
  }

  changeCartItemDescription(uuid: string, description: string) {
    const cartItem = findCartItem(this.draftState.cart, uuid);
    if (cartItem) {
      cartItem.description = description;
    }
  }

  changeCartItemQuestionOne(uuid: string, question: string) {
    const cartItem = findCartItem(this.draftState.cart, uuid);
    if (cartItem) {
      cartItem.question_1 = question;
    }
  }

  changeCartItemQuestionTwo(uuid: string, question: string) {
    const cartItem = findCartItem(this.draftState.cart, uuid);
    if (cartItem) {
      cartItem.question_2 = question;
    }
  }

  updateDescription(description: string) {
    this.draftState.description = description;
  }

  updateQuestionOne(value: string) {
    this.draftState.question1 = value;
  }

  updateQuestionTwo(value: string) {
    this.draftState.question2 = value;
  }

  updateContactDetailsValues(values: ContactDetailsValues) {
    this.draftState.contactDetails.values = values;
  }

  setServiceAreaDefault(zipCode: string) {
    const currentValue = this.draftState.contactDetails.values.zipCode;
    if (currentValue === undefined || currentValue === '') {
      this.draftState.contactDetails.values.zipCode = zipCode;
    }
  }

  updateContactDetailsValid(valid: boolean) {
    this.draftState.contactDetails.valid = valid;
  }

  startSubmittingContactDetailsForm() {
    this.draftState.contactDetails.submitting = true;
  }

  stopSubmittingContactDetailsForm() {
    this.draftState.contactDetails.submitting = false;
  }

  setSuccess() {
    this.draftState.success = true;
  }

  selectArrivalDate(date: string) {
    this.draftState.arrivalWindow.date = date;
  }

  selectArrivalTime(time: TimeSlot) {
    this.draftState.arrivalWindow.time = time;
  }

  resetArrivalTime() {
    this.draftState.arrivalWindow.time = undefined;
  }

  resetArrival() {
    this.draftState.arrivalWindow.date = undefined;
    this.draftState.arrivalWindow.time = undefined;
  }

  setUnavailableDateError(isError: boolean) {
    this.draftState.unavailableDateError = isError;
  }

  skipSteps(data: FormattedServicesData) {
    const { industries, pricingForms } = data;
    // has one industry with one category
    const skipFirstStep: boolean =
      industries?.length === 1 && industries?.[0]?.categories?.length === 1 && pricingForms?.length === 0;

    // as above + has one or none subcategories
    const subcategories = industries?.[0]?.categories?.[0]?.subcategories || [];
    const skipSecondStep: boolean = skipFirstStep && subcategories.length < 2;
    const alreadySkippedSteps = this.state.firstStepSkipped || this.state.secondStepSkipped;

    if (alreadySkippedSteps) {
      return;
    }

    if (skipFirstStep) {
      const industry = industries?.[0];
      const category = industry?.categories?.[0];
      const subcat = category?.subcategories?.[0];

      if (industry) this.selectIndustry(industry);
      if (category) this.selectCategory(category);

      this.draftState.firstStepSkipped = true;
      if (skipSecondStep) {
        this.selectSubCategory(subcat || undefined);
        this.draftState.secondStepSkipped = true;
      }

      this.changeStep(skipSecondStep ? 'services' : 'sub-categories');
    }

    if (industries.length === 0 && pricingForms && pricingForms.length === 1) {
      if (pricingForms[0]) {
        this.selectPricingForm(pricingForms[0]);
        this.changeStep('pricing-form');
      }
    }
  }

  addPhotosToService(serviceUuid: string, photos: File[]) {
    const cartItem = findCartItem(this.draftState.cart, serviceUuid);
    const preparedPhotos = photos.map((photo) => ({ isLoading: true, file: photo }));
    if (cartItem) {
      cartItem.photos = [...cartItem.photos, ...preparedPhotos];
    }
  }

  addPhoto(cartItemUUID: string, photo: Photo) {
    const cartItem = findCartItem(this.draftState.cart, cartItemUUID);
    if (cartItem) {
      cartItem.photos = [...cartItem.photos, photo];
    }
  }

  removePhoto(cartItemUUID: string, { file }: Photo) {
    const cartItem = findCartItem(this.draftState.cart, cartItemUUID);
    if (cartItem) {
      cartItem.photos = cartItem.photos.filter(
        (photo) => photo.file.name !== file.name && photo.file.size !== file.size
      );
    }
    this.draftState.photos = this.draftState.photos.filter((key) => !key.includes(file.name));
  }

  setErrorForPhoto(serviceUuid: string, file: File) {
    const cartItem = findCartItem(this.draftState.cart, serviceUuid);
    if (cartItem) {
      cartItem.photos = cartItem.photos.map((photo) => {
        if (photo.file.name === file.name && photo.file.size === file.size) {
          return { ...photo, isError: true };
        }
        return photo;
      });
    }
  }

  setLoadingForPhoto(serviceUuid: string, file: File, loading: boolean) {
    const cartItem = findCartItem(this.draftState.cart, serviceUuid);
    if (cartItem) {
      cartItem.photos = cartItem.photos.map((photo) => {
        if (photo.file.name === file.name && photo.file.size === file.size) {
          return { ...photo, isLoading: loading };
        }
        return photo;
      });
    }
  }

  attachPhoto(fileName: string, key: string) {
    this.draftState.photos.push(prepareAWSKey(fileName, key));
  }

  setPricingFormValues(uuid: string, values: FormValues) {
    const { pricingFormValues } = this.draftState;
    const pricingForm = pricingFormValues.find((item: PricingFormValue) => item.uuid === uuid);
    if (pricingForm) {
      pricingForm.values = values;
    } else {
      this.draftState.pricingFormValues.push({ uuid, values });
    }
  }

  setInitParams(params: InitMessageParams) {
    switch (params.source) {
      case 'customer_portal':
        if (params.token && params.customerUuid) {
          this.draftState.initParams = params;
          const address = params.addresses?.[0];
          this.draftState.contactDetails.values = {
            ...this.draftState.contactDetails.values,
            ...params.contactDetails,
            addressLine1: address?.streetAddress ?? '',
            addressLine2: address?.unit ?? '',
            state: address?.state ?? '',
            city: address?.city ?? '',
            zipCode: address?.zipCode ?? '',
            country: address?.country || UNITED_STATES,
          };
          this.draftState.contactDetails.valid = true;
        } else {
          setExtras({
            source: 'customer_portal',
            tokenType: typeof params.token,
            customerUUID: typeof params.customerUuid,
          });
          captureMessage('Invalid init params', Codeowners.CUSTOMER_INTAKE);
        }

        break;
      case 'preview':
        this.draftState.initParams = params;
        this.draftState.contactDetails = initialState.contactDetails;
        break;
      default:
        this.draftState.initParams = initialState.initParams;
        this.draftState.contactDetails = initialState.contactDetails;
        break;
    }
  }

  setAppOpened(opened: boolean) {
    this.draftState.appOpened = opened;
  }

  setPaymentDetails(paymentDetails: { last4: string }) {
    this.draftState.paymentDetails = {
      last4: paymentDetails.last4,
    };
  }
}
